import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

import { ApiError, apiFetch } from "./client";
import type { components } from "./schema";

export type SessionUser = components["schemas"]["SessionUser"];
export type Me = components["schemas"]["Me"];
type RegisterResponse = components["schemas"]["RegisterResponse"];
type PasskeyOptions = components["schemas"]["PasskeyOptions"];
type PasskeyRegistered = components["schemas"]["PasskeyRegistered"];

export interface Registered {
  user: SessionUser;
  recoveryCodes: string[];
}

export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn();
}

/** The browser dialog was dismissed or timed out: not an API error, but worth explaining. */
function asPasskeyError(error: unknown): never {
  if (error instanceof ApiError) throw error;
  throw new ApiError(0, "passkey_cancelled", "Passkey geannuleerd");
}

export function getMe(): Promise<Me> {
  return apiFetch<Me>("/me");
}

export function logout(): Promise<void> {
  return apiFetch<undefined>("/auth/logout", { method: "POST" });
}

export async function registerWithPassword(
  email: string,
  inviteCode: string,
  password: string,
): Promise<Registered> {
  const body = await apiFetch<RegisterResponse>("/auth/register", {
    method: "POST",
    json: { email, invite_code: inviteCode, password },
  });
  return { user: body.user, recoveryCodes: body.recovery_codes };
}

export async function registerWithPasskey(email: string, inviteCode: string): Promise<Registered> {
  const { challenge_id, options } = await apiFetch<PasskeyOptions>(
    "/auth/passkey/register/options",
    { method: "POST", json: { email, invite_code: inviteCode } },
  );
  let credential;
  try {
    credential = await startRegistration({
      optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
    });
  } catch (error) {
    asPasskeyError(error);
  }
  const body = await apiFetch<PasskeyRegistered>("/auth/passkey/register/verify", {
    method: "POST",
    json: { challenge_id, credential, name: "Passkey" },
  });
  return { user: body.user, recoveryCodes: body.recovery_codes ?? [] };
}

export function loginWithPassword(email: string, password: string): Promise<SessionUser> {
  return apiFetch<SessionUser>("/auth/login", { method: "POST", json: { email, password } });
}

export async function loginWithPasskey(): Promise<SessionUser> {
  const { challenge_id, options } = await apiFetch<PasskeyOptions>("/auth/passkey/login/options", {
    method: "POST",
    json: {},
  });
  let credential;
  try {
    credential = await startAuthentication({
      optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
    });
  } catch (error) {
    asPasskeyError(error);
  }
  return apiFetch<SessionUser>("/auth/passkey/login/verify", {
    method: "POST",
    json: { challenge_id, credential },
  });
}

export function recover(
  email: string,
  recoveryCode: string,
  newPassword: string | null,
): Promise<SessionUser> {
  return apiFetch<SessionUser>("/auth/recover", {
    method: "POST",
    json: { email, recovery_code: recoveryCode, new_password: newPassword },
  });
}
